import { action, computed, makeObservable, observable } from 'mobx';
import { QuickBetState } from 'src/domains/sportsbook/betting/betSlipState/quickBetState/QuickBetState';

export type UnavailableSelectionLabels = {
    selectionName: string;
    marketName: string;
};

export type UnavailablePendingItem = {
    selectionId: string;
    selectionName: string;
    marketName: string;
};

export class UnavailableSelectionsState {
    @observable private readonly snapshots: Map<string, UnavailableSelectionLabels> = new Map();
    @observable public pendingRemoved: Array<UnavailablePendingItem> = [];

    public constructor(
        private readonly quickBetState: QuickBetState,
        private readonly getLegsCount: () => number
    ) {
        makeObservable(this);
    }

    @computed public get isWarning(): boolean {
        return this.pendingRemoved.length > 0;
    }

    @computed public get messages(): Array<string> {
        return this.pendingRemoved.map((item) => `${item.selectionName} no longer available`);
    }

    @action public onLegAdded = (selectionId: string, labels: UnavailableSelectionLabels): void => {
        this.snapshots.set(selectionId, labels);
    };

    @action public onSystemRemoved = (
        selectionId: string,
        fallbackLabels: UnavailableSelectionLabels | null = null
    ): void => {
        if (this.pendingRemoved.some((item) => item.selectionId === selectionId)) {
            return;
        }

        const labels = this.snapshots.get(selectionId) ??
            fallbackLabels ?? { selectionName: 'Selection', marketName: '' };

        this.snapshots.delete(selectionId);
        this.pendingRemoved.push({
            selectionId,
            selectionName: labels.selectionName,
            marketName: labels.marketName,
        });
    };

    @action public onUserRemoved = (selectionId: string): void => {
        this.snapshots.delete(selectionId);
    };

    @action public accept = (): void => {
        this.pendingRemoved = [];

        if (this.getLegsCount() === 0) {
            this.quickBetState.onUpdateQuickBet(false);
        }
    };

    @action public reset = (): void => {
        this.snapshots.clear();
        this.pendingRemoved = [];
    };
}
